import { ChevronRight, ChevronLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { StepProps } from "../types";
import { cn } from "@/lib/utils";

const formatSalary = (value: string) => {
  // Remove all non-digits
  const digits = value.replace(/\D/g, "");

  // If empty after removing non-digits, return empty string
  if (!digits) {
    return "";
  }

  // Format as Swedish currency
  const formatted = new Intl.NumberFormat("sv-SE", {
    style: "currency",
    currency: "SEK",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(digits));

  return formatted;
};

const BENEFITS = [
  "Kollektivavtal",
  "Utbildning",
  "Friskvårdsbidrag",
  "Workation",
  "Hunddagis",
  "Bonus",
  "30 dagars semester",
  "Kompetensutveckling",
  "Företagsaktiviteter",
  "Tjänstebil",
  "Lunchbidrag",
  "Provision",
  "Sjukvårdsbidrag",
  "Optionsprogram",
];

interface StepThreeProps extends StepProps {
  salary: string;
  onSalaryChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  payTransparency: boolean;
  onPayTransparencyChange: (value: boolean) => void;
  location: string;
  onLocationChange: (value: string) => void;
  isRemote: boolean;
  onRemoteChange: (value: boolean) => void;
  experience: string;
  onExperienceChange: (value: string) => void;
  formOfEmployment: string;
  onFormOfEmploymentChange: (value: string) => void;
  selectedBenefits: string[];
  onBenefitsChange: (benefits: string[]) => void;
  swedishLevel: string;
  onSwedishLevelChange: (level: string) => void;
  englishLevel: string;
  onEnglishLevelChange: (level: string) => void;
  driversLicense: string;
  onDriversLicenseChange: (value: string) => void;
  onBack?: () => void;
  onForward?: () => void;
  canGoBack?: boolean;
  canGoForward?: boolean;
}

export const StepThree = ({
  onNext,
  salary,
  onSalaryChange,
  payTransparency,
  onPayTransparencyChange,
  location,
  onLocationChange,
  isRemote,
  onRemoteChange,
  experience,
  onExperienceChange,
  formOfEmployment,
  onFormOfEmploymentChange,
  selectedBenefits,
  onBenefitsChange,
  swedishLevel,
  onSwedishLevelChange,
  englishLevel,
  onEnglishLevelChange,
  driversLicense,
  onDriversLicenseChange,
  onBack,
  canGoBack = true,
  canGoForward = true,
}: StepThreeProps) => {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <div className="h-5 w-5 rounded-full bg-primary flex items-center justify-center text-white text-sm font-medium">
          3
        </div>
        <h2 className="text-xl font-semibold mb-0">Ange detaljer</h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label htmlFor="salary">
            Lön (grundlön + provision efter 6 månader)
          </Label>
          <Input
            id="salary"
            type="text"
            value={salary}
            onChange={(e) => {
              const formattedValue = formatSalary(e.target.value);
              onSalaryChange({
                ...e,
                target: { ...e.target, value: formattedValue },
              });
            }}
            onKeyDown={(e) => {
              // Allow backspace to work by temporarily removing formatting
              if (e.key === "Backspace") {
                const digits = salary.replace(/\D/g, "");
                const newDigits = digits.slice(0, -1);
                const formattedValue = formatSalary(newDigits);
                const event = {
                  target: {
                    value: formattedValue,
                  },
                } as React.ChangeEvent<HTMLInputElement>;
                onSalaryChange(event);
                e.preventDefault();
              }
            }}
            placeholder="T.ex. 42 000 kr"
            className="font-mono"
          />
        </div>

        <div className="col-span-1 md:col-span-2 flex items-center justify-between rounded-lg border p-4">
          <div className="space-y-0.5">
            <Label htmlFor="pay-transparency" className="text-base cursor-pointer">
              Visa lön i annonsen
            </Label>
            <p className="text-sm text-muted-foreground">
              Lön visas för sökande (rekommenderas enligt lönetransparensdirektivet)
            </p>
          </div>
          <Switch
            id="pay-transparency"
            checked={payTransparency}
            onCheckedChange={onPayTransparencyChange}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="location">Plats</Label>
          <Select value={location} onValueChange={onLocationChange}>
            <SelectTrigger>
              <SelectValue placeholder="Välj plats" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Stockholm">Stockholm</SelectItem>
              <SelectItem value="Göteborg">Göteborg</SelectItem>
              <SelectItem value="Malmö">Malmö</SelectItem>
              <SelectItem value="Uppsala">Uppsala</SelectItem>
              <SelectItem value="Västerås">Västerås</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="experience">Erfarenhet</Label>
          <Select value={experience} onValueChange={onExperienceChange}>
            <SelectTrigger>
              <SelectValue placeholder="Välj erfarenhetsnivå" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="0+ år">0+ år</SelectItem>
              <SelectItem value="1+ år">1+ år</SelectItem>
              <SelectItem value="3+ år">3+ år</SelectItem>
              <SelectItem value="5+ år">5+ år</SelectItem>
              <SelectItem value="10+ år">10+ år</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="employment">Anställningsform</Label>
          <Select
            value={formOfEmployment}
            onValueChange={onFormOfEmploymentChange}
          >
            <SelectTrigger>
              <SelectValue placeholder="Välj anställningsform" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Heltid">Heltid</SelectItem>
              <SelectItem value="Deltid">Deltid</SelectItem>
              <SelectItem value="Projektanställning">
                Projektanställning
              </SelectItem>
              <SelectItem value="Praktik">Praktik</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="col-span-1 md:col-span-2 flex items-center space-x-2">
          <input
            type="checkbox"
            id="remote"
            checked={isRemote}
            onChange={(e) => onRemoteChange(e.target.checked)}
            className="rounded border-gray-300 focus:ring-primary"
          />
          <Label htmlFor="remote" className="text-sm cursor-pointer">
            Distansarbete möjligt
          </Label>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="space-y-2">
        <Label>Förmåner (välj 2-4)</Label>
        <div className="flex flex-wrap gap-1.5">
          {BENEFITS.map((benefit) => (
            <Badge
              key={benefit}
              variant={
                selectedBenefits.includes(benefit) ? "default" : "outline"
              }
              className={cn(
                "cursor-pointer transition-colors text-xs",
                selectedBenefits.includes(benefit)
                  ? "bg-primary hover:bg-primary/80"
                  : "hover:bg-primary/10"
              )}
              onClick={() => {
                if (selectedBenefits.includes(benefit)) {
                  onBenefitsChange(
                    selectedBenefits.filter((b) => b !== benefit)
                  );
                } else if (selectedBenefits.length < 4) {
                  onBenefitsChange([...selectedBenefits, benefit]);
                }
              }}
            >
              {benefit}
            </Badge>
          ))}
        </div>
        {selectedBenefits.length > 0 && (
          <p className="text-xs text-muted-foreground">
            Valda förmåner: {selectedBenefits.join(", ")}
          </p>
        )}
      </div>

      {/* Language Skills Section */}
      <div className="space-y-2">
        <Label>Språkkunskaper</Label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label htmlFor="swedish">Svenska</Label>
            <Select value={swedishLevel} onValueChange={onSwedishLevelChange}>
              <SelectTrigger>
                <SelectValue placeholder="Välj nivå" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Nybörjare">Nybörjare</SelectItem>
                <SelectItem value="Medelnivå">Medelnivå</SelectItem>
                <SelectItem value="Flytande">Flytande</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="english">Engelska</Label>
            <Select value={englishLevel} onValueChange={onEnglishLevelChange}>
              <SelectTrigger>
                <SelectValue placeholder="Välj nivå" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Nybörjare">Nybörjare</SelectItem>
                <SelectItem value="Medelnivå">Medelnivå</SelectItem>
                <SelectItem value="Flytande">Flytande</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Driver's License Section */}
      <div className="space-y-2">
        <Label htmlFor="driversLicense">Krav på körkort?</Label>
        <Select value={driversLicense} onValueChange={onDriversLicenseChange}>
          <SelectTrigger>
            <SelectValue placeholder="Välj krav" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Ja">Ja</SelectItem>
            <SelectItem value="Nej">Nej</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex justify-between items-center pt-4 border-t">
        <Button
          variant="outline"
          onClick={onBack}
          disabled={!canGoBack}
          className="flex items-center gap-2"
        >
          <ChevronLeft className="h-4 w-4" />
          Föregående
        </Button>

        <div className="flex gap-2">
          <Button
            onClick={onNext}
            disabled={
              !canGoForward ||
              !salary.trim() ||
              !location.trim() ||
              !experience.trim() ||
              !formOfEmployment.trim() ||
              !swedishLevel.trim() ||
              !englishLevel.trim()
            }
            className="flex items-center gap-2"
          >
            Nästa
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default StepThree;
